import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { MarketActionCard, MarketList, MarketRow } from '@market/react';
import { getLocalePath } from 'utilities/locales';
import { checkAddOnsForActiveSubscription } from 'components/pages/product/product_with_software_add_on/utils';
import { getPlanPrice } from 'utilities/software';

const NO_ADD_ON_VALUE = '0';

const SoftwareAddOn = ({
  category,
  addOns,
  selected = NO_ADD_ON_VALUE,
  onSelect = () => {},
  yesAsFirstOption = false, // if we want it to be yes/no vs no/yes
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  if (addOns.length === 0) {
    return null;
  }

  if (checkAddOnsForActiveSubscription(addOns)) {
    return null;
  }

  const renderAddonSubtext = () => {
    const linkKey = `relations.addOn.subtext.${category}`;

    return i18n.exists(linkKey) ? (
      <p
        dangerouslySetInnerHTML={{
          __html: t(linkKey, { locale: getLocalePath() }),
        }}
        className="paragraph-20"
        style={{ marginTop: theme.spacing(1) }}
      />
    ) : null;
  };

  const renderAddons = () => {
    const NoOption = (
      <MarketActionCard value={NO_ADD_ON_VALUE}>
        <MarketRow>
          <p slot="label">{t(`relations.addOn.noText.${category}`)}</p>
        </MarketRow>
      </MarketActionCard>
    );

    // Add on option categories can have more than one option
    const YesOptions = addOns.map((addOn) => (
      <MarketActionCard
        key={addOn.variant_id.toString()}
        value={addOn.variant_id.toString()}
      >
        <MarketRow>
          <p slot="label">{t('productPage.addSoftwareAddOn')}</p>
          <p
            slot="side-label"
            className="medium-30"
            style={{ whiteSpace: 'nowrap' }}
          >
            {/* this override stays due to the fact that we want a custom pricing display on the pdp vs the cart */}
            {`+${t(`relations.addOnOverride.${addOn.base_product_key}.price`, {
              price: getPlanPrice(addOn.plan),
            })}`}
          </p>
        </MarketRow>
      </MarketActionCard>
    ));

    const options = yesAsFirstOption
      ? [YesOptions, NoOption]
      : [NoOption, YesOptions];

    return options.flat();
  };

  return (
    <div key={category}>
      <p className="medium-30" style={{ margin: theme.spacing(2, 0) }}>
        {t(`relations.addOn.label.${category}`)}
      </p>
      <MarketList
        interactive
        value={selected}
        onMarketListSelectionsDidChange={(event) => {
          const {
            detail: { newSelection, newSelectionValue },
            target,
          } = event;

          onSelect({
            category: target.dataset.category,
            newVariantId: newSelectionValue,
            choice: newSelection.innerText,
          });
        }}
        data-category={category}
        data-testid="add-on-list"
      >
        {renderAddons()}
        {renderAddonSubtext()}
      </MarketList>
    </div>
  );
};

export { SoftwareAddOn as default, NO_ADD_ON_VALUE };
